import { TradingToast, useToast } from '@trading-ui/core';
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getPricingIndices,
  createPricingIndex,
  updatePricingIndex,
  deletePricingIndex,
  type PricingIndex,
  type CreatePricingIndexPayload,
} from '@/api/services/settings/pricing-indices.service';

import { QUERY_KEYS } from '@/api/config/query-keys';

import { parseBackendReason } from '@/utils/error.utils';

import type { ProcessedBackendError } from '@/interfaces';

export const usePricingIndices = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const [pricingIndices, setPricingIndices] = useState<PricingIndex[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data, isLoading, error, isRefetching } = useQuery({
    queryKey: [QUERY_KEYS.PRICING_INDICES],
    queryFn: () => getPricingIndices(),
  });

  useEffect(() => {
    if (data && !isLoading) {
      setPricingIndices(data);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (error) {
      const errorMessage = parseBackendReason(error as Error | ProcessedBackendError);
      addToast(<TradingToast type="error" title="Error" message={errorMessage} />);
    }
  }, [error, addToast]);

  const handleCreatePricingIndex = useCallback(
    async (payload: CreatePricingIndexPayload) => {
      try {
        await createPricingIndex(payload);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRICING_INDICES] });
        addToast(<TradingToast message={t('services.pricingIndices.createSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} title="Error" type="error" />);
      }
    },
    [queryClient, addToast, t]
  );

  const handleUpdatePricingIndex = useCallback(
    async (id: number, payload: Partial<CreatePricingIndexPayload>) => {
      try {
        await updatePricingIndex(id, payload);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRICING_INDICES] });
        // Invalidate masters to refresh plants pricing index data
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BIOFLUX_MASTERS] });
        addToast(<TradingToast message={t('services.pricingIndices.updateSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} title="Error" type="error" />);
      }
    },
    [queryClient, addToast, t]
  );

  const handleDeletePricingIndex = useCallback(
    async (id: number) => {
      try {
        setDeletingId(id);
        await deletePricingIndex(id);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRICING_INDICES] });
        // Invalidate masters to refresh plants pricing index data
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BIOFLUX_MASTERS] });
        addToast(<TradingToast message={t('services.pricingIndices.deleteSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} title="Error" type="error" />);
      } finally {
        setDeletingId(null);
      }
    },
    [queryClient, addToast, t]
  );

  return {
    pricingIndices,
    isLoading,
    isRefetching,
    deletingId,
    handleCreatePricingIndex,
    handleUpdatePricingIndex,
    handleDeletePricingIndex,
  };
};

export default usePricingIndices;
